/**
 * 004 — Plantilla "Boda Elegante" — port FIEL de templates/boda-elegante/BodaElegante.dc.html.
 * Paleta marfil/oro champán, caligrafía Great Vibes + Cormorant Garamond. Independiente
 * de los otros estilos (pixel-a-pixel). Datos vía el payload canónico + helpers.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  CoverVeil, Photo, DateBlock, Countdown, GuestPass, Icon, Button, Itinerary,
  DressCode, NoteCard, GiftCard, GiftList, RsvpForm, Monogram, Filigree,
} from '@/invitation/ds';
import { deriveFields } from '@/invitation/payload/derived';
import { EVENTO_META, ROTULOS, NOTA_NINOS, NOTA_FOTOS, REGALO_TARJETAS, vestimenta as vestimentaCat } from '@/invitation/payload/system-catalog';
import {
  resolveHistoriaTexto, resolveHistoriaFrase, resolveRegalosMensaje, resolveEventoNota,
  resolveMomento, resolveVestimentaCopy,
} from '@/invitation/payload/resolve';
import type { TemplateProps } from './types';
import { orderedCouple } from '@/invitation/render/coupleOrder';
import { buildCalendarUrlFromPayload } from '@/invitation/render/calendarUrl';
import RsvpCerrado from '@/invitation/render/RsvpCerrado';

// ---- Paleta Elegante (hardcodeada; este estilo es independiente) ------------
const C = {
  bg: '#f8f2e9',
  ink: '#2e2620',
  gold: '#b08d4f',
  goldSoft: '#c9ab74',
  goldLight: '#d8bd87',
  sec: '#6f6157',
  muted: '#a3968a',
  dark: '#221913',
  onDark: '#f4e9d4',
  onDarkSoft: 'rgba(240,227,202,.62)',
};
const F = {
  script: "'Great Vibes', cursive",
  serif: "'Cormorant Garamond', serif",
  caps: "'Tenor Sans', sans-serif",
  body: "'Jost', sans-serif",
};

// ---- Helpers de composición -------------------------------------------------
function Diamond({ color = C.goldSoft, max = 200 }: { color?: string; max?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, width: '100%', maxWidth: max, margin: '0 auto' }}>
      <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${color})` }} />
      <span style={{ width: 5, height: 5, background: color, transform: 'rotate(45deg)', flex: 'none' }} />
      <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color},transparent)` }} />
    </div>
  );
}
function Script({ children, size = 'clamp(34px,4vw,54px)', color = C.gold }: { children: ReactNode; size?: string; color?: string }) {
  return <p style={{ margin: 0, fontFamily: F.script, fontWeight: 400, fontSize: size, lineHeight: 1.28, padding: '0 .1em .12em', color }}>{children}</p>;
}
const wrap = (extra?: CSSProperties): CSSProperties => ({ position: 'relative', maxWidth: 1080, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(20px,2.4vw,32px)', textAlign: 'center', ...extra });
const SEC_PAD = 'clamp(56px,6.4vw,102px) clamp(24px,6vw,96px)';

const BodaElegante = ({ payload, open = true, onEnter, onRsvp, rsvpSubmitted, rsvpClosed, coordinador, album }: TemplateProps) => {
  const p = payload;
  const i = p.interruptores;
  const d = deriveFields(p.boda.fecha, p.novia.nombreCorto, p.novio.nombreCorto);
  const galeria = p.fotos?.galeria ?? [];
  const sede = p.fotos?.sedes ?? {};
  const portadaImg = p.fotos?.portada || galeria[0];
  const parejaImg = p.fotos?.pareja || galeria[1] || galeria[0];

  const { primero: n1, segundo: n2 } = orderedCouple(p);
  const calendarUrl = buildCalendarUrlFromPayload(p);
  const Names = (
    <>{n1.nombreCorto} <span style={{ fontSize: '.6em', color: C.goldLight, verticalAlign: '.14em' }}>&amp;</span> {n2.nombreCorto}</>
  );
  const openMap = (url?: string) => url && window.open(url, '_blank');
  const RATIOS = ['2 / 3', '3 / 2', '1 / 1', '4 / 5', '3 / 2', '2 / 3', '16 / 10', '5 / 4'];

  return (
    <div className="theme-elegante" style={{ background: C.bg, color: C.ink, fontFamily: F.body, fontWeight: 300, overflowX: 'hidden' }}>
      <style>{`
        .ige-split{grid-template-columns:1fr}
        .ige-w3{grid-template-columns:1fr}
        .ige-masonry{columns:1}
        .ige-fam{grid-template-columns:1fr}
        @media(min-width:560px){.ige-masonry{columns:2}.ige-fam{grid-template-columns:repeat(2,1fr)}}
        @media(min-width:900px){
          .ige-split{grid-template-columns:1.15fr .85fr}
          .ige-w3{grid-template-columns:1fr auto 1fr}
          .ige-masonry{columns:3}
        }
      `}</style>
      {/* 1 · Sobre */}
      <CoverVeil names={Names} eyebrow={ROTULOS.sobreEyebrow} date={d.fechaLarga} image={portadaImg} cta={ROTULOS.sobreCta} onEnter={onEnter} open={open} />

      {/* 2 · Portada — hero a sangre */}
      <section style={{ position: 'relative', minHeight: '92svh', display: 'grid', alignItems: 'end', justifyItems: 'center', background: C.dark, overflow: 'hidden', padding: 'clamp(64px,9vw,120px) clamp(24px,6vw,96px) clamp(88px,10vw,140px)' }}>
        {portadaImg && <div style={{ position: 'absolute', inset: 0 }}><Photo fill src={portadaImg} alt="" /></div>}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(78% 58% at 50% 30%,rgba(34,25,19,.12),rgba(28,20,15,.62) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(24,17,12,.42) 0%,transparent 26%,rgba(24,17,12,.34) 52%,rgba(24,17,12,.86) 100%)' }} />
        <div style={{ position: 'relative', textAlign: 'center', display: 'grid', gap: 'clamp(18px,2.2vw,30px)', justifyItems: 'center', maxWidth: 1040 }}>
          <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', letterSpacing: '.02em', color: 'rgba(240,227,202,.72)' }}>{ROTULOS.portadaFrase}</p>
          {/* h2: el h1 de la página es el del CoverVeil (mismos nombres). Evita el
              duplicado de <h1>. Estilos inline → sin cambio visual. */}
          <h2 style={{ margin: 0, fontFamily: F.script, fontWeight: 400, fontSize: 'clamp(48px,12.5vw,196px)', lineHeight: .94, color: C.onDark, textShadow: '0 22px 60px rgba(0,0,0,.35)' }}>{Names}</h2>
          <Diamond color="rgba(240,227,202,.5)" max={300} />
          {d.valida && <p style={{ margin: 0, fontFamily: F.caps, fontSize: 'clamp(10px,.92vw,13px)', letterSpacing: '.38em', textTransform: 'uppercase', color: 'rgba(240,227,202,.72)' }}>{d.fechaNumerica}</p>}
        </div>
      </section>

      {/* 3 · Nuestra historia */}
      <section style={{ position: 'relative', padding: SEC_PAD, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '6%', left: -70, color: C.gold, opacity: .07, pointerEvents: 'none' }}><Filigree name="guirnalda" width={420} /></div>
        <div style={{ position: 'absolute', bottom: '8%', right: -70, color: C.gold, opacity: .07, pointerEvents: 'none' }}><Filigree name="guirnalda" width={420} flipX /></div>
        <div style={wrap({ maxWidth: 740 })}>
          <Script>{ROTULOS.historia}</Script>
          <Diamond />
          <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontWeight: 300, fontSize: 'clamp(20px,2vw,29px)', lineHeight: 1.78, color: C.sec }}>{resolveHistoriaTexto(p.historia?.texto)}</p>
        </div>
      </section>

      {/* 4 · Retratos */}
      {i.retratos === 'pareja' && parejaImg && (
        <section style={{ position: 'relative', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div className="ige-split" style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'center' }}>
            <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', boxShadow: '0 46px 70px -54px rgba(80,58,38,.75)' }}><Photo ratio="2 / 3" shape="arch" src={parejaImg} alt="" /></div>
            <div style={{ display: 'grid', gap: 'clamp(16px,1.8vw,26px)', justifyItems: 'start', textAlign: 'left' }}>
              <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(16px,1.4vw,20px)', color: C.muted }}>Los novios</p>
              <p style={{ margin: 0, fontFamily: F.script, fontSize: 'clamp(46px,5.4vw,82px)', lineHeight: 1.16, padding: '0 .1em .1em', color: C.ink }}>
                {n1.nombreCompleto || n1.nombreCorto}<br /><span style={{ color: C.gold, fontSize: '.72em' }}>&amp;</span> {n2.nombreCompleto || n2.nombreCorto}
              </p>
              <span style={{ width: 'min(220px,50%)', height: 1, background: `linear-gradient(90deg,${C.goldSoft},transparent)` }} />
              <p style={{ margin: 0, maxWidth: 400, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', lineHeight: 1.8, color: C.sec }}>{resolveHistoriaFrase(p.historia?.frase)}</p>
            </div>
          </div>
        </section>
      )}
      {i.retratos === 'individuales' && (p.fotos?.novia || p.fotos?.novio) && (
        <section style={{ padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div className="ige-w3" style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 'clamp(24px,2.8vw,42px)', alignItems: 'center' }}>
            <figure style={{ margin: 0, display: 'grid', gap: 22, justifyItems: 'center' }}>
              {p.fotos?.novia && <div style={{ width: '100%', maxWidth: 250, boxShadow: '0 40px 62px -50px rgba(80,58,38,.7)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.novia} alt="" /></div>}
              <figcaption style={{ textAlign: 'center', display: 'grid', gap: 6 }}>
                <span style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 16, color: C.muted }}>La novia</span>
                <span style={{ fontFamily: F.script, fontSize: 'clamp(32px,3vw,44px)', color: C.ink }}>{p.novia.nombreCompleto || p.novia.nombreCorto}</span>
              </figcaption>
            </figure>
            <div style={{ display: 'grid', justifyItems: 'center', gap: 14 }}>
              <span style={{ width: 1, height: 'clamp(0px,5vw,74px)', background: 'linear-gradient(180deg,transparent,#dfd0ba)' }} />
              <span style={{ fontFamily: F.script, fontSize: 'clamp(50px,5.6vw,82px)', color: C.gold }}>&amp;</span>
              <span style={{ width: 1, height: 'clamp(0px,5vw,74px)', background: 'linear-gradient(180deg,#dfd0ba,transparent)' }} />
            </div>
            <figure style={{ margin: 0, display: 'grid', gap: 22, justifyItems: 'center' }}>
              {p.fotos?.novio && <div style={{ width: '100%', maxWidth: 250, boxShadow: '0 40px 62px -50px rgba(80,58,38,.7)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.novio} alt="" /></div>}
              <figcaption style={{ textAlign: 'center', display: 'grid', gap: 6 }}>
                <span style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 16, color: C.muted }}>El novio</span>
                <span style={{ fontFamily: F.script, fontSize: 'clamp(32px,3vw,44px)', color: C.ink }}>{p.novio.nombreCompleto || p.novio.nombreCorto}</span>
              </figcaption>
            </figure>
          </div>
        </section>
      )}

      {/* 5 · Reserva la fecha */}
      {d.valida && (
        <section style={{ position: 'relative', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: C.gold, opacity: .05, pointerEvents: 'none' }}><Filigree name="laurel-corona" width={430} /></div>
          <div style={wrap({ maxWidth: 820 })}>
            <Script>{ROTULOS.reservaFecha}</Script>
            <Diamond />
            <DateBlock weekday={d.diaSemana} day={d.dia} monthYear={`${d.mesNombre} ${d.anio}`} time={d.horaEnLetra} />
            {p.boda.fecha && <div style={{ width: '100%', maxWidth: 440 }}><Countdown target={p.boda.fecha} /></div>}
            {calendarUrl && <Button variant="outline" size="md" onClick={() => window.open(calendarUrl, '_blank')}>{ROTULOS.reservaCta}</Button>}
          </div>
        </section>
      )}

      {/* 6 · Pase (🔗) */}
      {p.invitado && (
        <section style={{ padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div style={{ maxWidth: 560, margin: '0 auto' }}><GuestPass guest={p.invitado.nombre} passes={p.invitado.pases} /></div>
        </section>
      )}

      {/* 7 · Con la bendición de */}
      {p.familia && p.familia.length > 0 && (
        <section style={{ position: 'relative', overflow: 'hidden', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', textAlign: 'center' }}>
          <div style={{ position: 'absolute', top: '14%', right: -40, color: C.gold, opacity: .06, pointerEvents: 'none' }}><Filigree name="rama-botanica" width={360} /></div>
          <div style={wrap({ maxWidth: 1080 })}>
            <Script size="clamp(32px,3.6vw,48px)">{ROTULOS.bendicion}</Script>
            <Diamond />
            <div className="ige-fam" style={{ width: '100%', display: 'grid', gap: 'clamp(20px,2.2vw,30px)', marginTop: 8, maxWidth: 720 }}>
              {p.familia.map((f, idx) => (
                <div key={idx} style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 16, color: C.muted }}>{f.rol}</span>
                  <span style={{ fontFamily: F.serif, fontSize: 'clamp(18px,1.6vw,22px)', lineHeight: 1.6, color: '#3b322b' }}>{f.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 8 · Eventos (fondo oscuro) */}
      {p.eventos && p.eventos.length > 0 && (
        <section style={{ position: 'relative', padding: SEC_PAD, background: C.dark, color: C.onDark, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(60% 50% at 50% 0%,rgba(216,189,135,.14),transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(30px,3.2vw,46px)' }}>
            <div style={{ display: 'grid', justifyItems: 'center', gap: 18, textAlign: 'center', width: '100%' }}>
              <Script size="clamp(36px,4.2vw,58px)" color={C.goldLight}>{ROTULOS.eventos}</Script>
              <Diamond color={C.goldLight} />
            </div>
            <div style={{ width: '100%', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'start', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))' }}>
              {p.eventos.map((e, idx) => (
                <article key={idx} style={{ display: 'grid', gap: 22, justifyItems: 'center', textAlign: 'center' }}>
                  {i.fotosLugares && sede[e.tipo] && <div style={{ width: '100%', maxWidth: 270, margin: '0 auto', boxShadow: '0 40px 60px -50px rgba(0,0,0,.85)' }}><Photo ratio="2 / 3" shape="arch" src={sede[e.tipo]} alt="" /></div>}
                  <Icon name={EVENTO_META[e.tipo].icon} size={30} stroke={C.goldLight} />
                  <h3 style={{ margin: 0, fontFamily: F.script, fontWeight: 400, fontSize: 'clamp(32px,3.4vw,46px)', lineHeight: 1.28, padding: '0 .1em .12em', color: C.onDark }}>{EVENTO_META[e.tipo].titulo}</h3>
                  {e.hora && <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: C.goldLight }}>{e.hora}</p>}
                  {e.sede && <p style={{ margin: 0, fontFamily: F.serif, fontSize: 'clamp(18px,1.6vw,23px)', lineHeight: 1.5 }}>{e.sede}</p>}
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.9, color: 'rgba(240,227,202,.62)', maxWidth: 290 }}>
                    {e.direccion}{e.direccion && <br />}<em style={{ fontFamily: F.serif, fontSize: 16 }}>{resolveEventoNota(e.tipo, e.nota)}</em>
                  </p>
                  {e.mapaUrl && <Button variant="outline" size="sm" tone="onDark" onClick={() => openMap(e.mapaUrl)}>{ROTULOS.eventoAccion}</Button>}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 9 · Itinerario (horizontal) */}
      {p.itinerario && p.itinerario.length > 0 && (
        <section style={{ overflow: 'hidden', padding: SEC_PAD }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(26px,2.8vw,38px)' }}>
            <Script>{ROTULOS.itinerario}</Script>
            <Diamond />
            <div style={{ width: '100%' }}>
              <Itinerary layout="horizontal" steps={p.itinerario.map((it) => { const m = resolveMomento(it.momento, it.momentoLibre); return { icon: m.icon, time: it.hora, label: m.nombre, detail: it.detalle }; })} />
            </div>
          </div>
        </section>
      )}

      {/* 10 · Vestimenta (split con foto si hay) */}
      {p.vestimenta?.codigo && (
        <section style={{ position: 'relative', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(46% 44% at 78% 40%,rgba(199,150,138,.16),transparent 70%)', pointerEvents: 'none' }} />
          {i.fotoVestimenta && p.fotos?.vestimenta ? (
            <div style={{ position: 'relative', maxWidth: 1040, margin: '0 auto', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'center', gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ width: '100%', maxWidth: 300, margin: '0 auto', boxShadow: '0 46px 70px -54px rgba(80,58,38,.7)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.vestimenta} alt="" /></div>
              <div style={{ display: 'grid', justifyItems: 'center', gap: 26 }}>
                <Script size="clamp(32px,3.6vw,48px)">{ROTULOS.vestimenta}</Script>
                <Diamond />
                <DressCode code={vestimentaCat(p.vestimenta.codigo)?.nombre ?? p.vestimenta.codigo} note={resolveVestimentaCopy(p.vestimenta.codigo)} palette={i.coloresSugeridos ? (p.vestimenta.paleta ?? []) : []} avoid={i.coloresReservados ? (p.vestimenta.reservados ?? []) : []} />
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 26 }}>
              <Script size="clamp(32px,3.6vw,48px)">{ROTULOS.vestimenta}</Script>
              <DressCode code={vestimentaCat(p.vestimenta.codigo)?.nombre ?? p.vestimenta.codigo} note={resolveVestimentaCopy(p.vestimenta.codigo)} palette={i.coloresSugeridos ? (p.vestimenta.paleta ?? []) : []} avoid={i.coloresReservados ? (p.vestimenta.reservados ?? []) : []} />
            </div>
          )}
        </section>
      )}

      {/* 11 · Galería (masonry) */}
      {i.galeria && galeria.length > 0 && (
        <section style={{ padding: '0 0 clamp(56px,6.4vw,102px)' }}>
          <div style={{ display: 'grid', justifyItems: 'center', gap: 10, textAlign: 'center', padding: '0 clamp(24px,6vw,110px) clamp(24px,2.6vw,36px)' }}>
            <Script>{ROTULOS.galeria}</Script>
            <Diamond />
          </div>
          <div className="ige-masonry" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 clamp(12px,2.4vw,42px)', columnGap: 10 }}>
            {galeria.map((src, idx) => (
              <div key={idx} style={{ breakInside: 'avoid', marginBottom: 10 }}>
                <Photo ratio={RATIOS[idx % RATIOS.length]} src={src} alt="" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 12 · Notas */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
        <div style={{ position: 'absolute', top: '44%', left: '50%', transform: 'translate(-50%,-50%)', color: C.gold, opacity: .05, pointerEvents: 'none' }}><Filigree name="anillos" width={300} /></div>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gap: 'clamp(24px,2.8vw,42px)', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
          <NoteCard icon="baby" title={i.ninosPermitidos ? 'Los niños son bienvenidos' : 'Sin niños'} body={i.ninosPermitidos ? NOTA_NINOS.permitidos : NOTA_NINOS.soloAdultos} framed={false} />
          {i.fotosInvitados && <NoteCard icon="camera" title="Comparte tus fotos" body={NOTA_FOTOS} hashtag={p.album?.hashtag} framed={false} />}
        </div>
        {i.fotosInvitados && album}
      </section>

      {/* 13 · Mesa de regalos */}
      {i.regalos && (
        <section style={{ position: 'relative', padding: 'clamp(52px,5.8vw,90px) clamp(24px,6vw,110px)', background: 'linear-gradient(180deg,#f3e7d3,#f8f2e9)' }}>
          <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(22px,2.4vw,34px)', textAlign: 'center' }}>
            <Script size="clamp(32px,3.8vw,50px)" color="#8a6d3b">{ROTULOS.regalos}</Script>
            <Diamond />
            <p style={{ margin: 0, maxWidth: 540, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(18px,1.7vw,25px)', lineHeight: 1.75, color: C.sec }}>{resolveRegalosMensaje(p.regalos?.mensaje)}</p>
            <div style={{ width: '100%', display: 'grid', gap: 'clamp(20px,2.4vw,32px)', alignItems: 'stretch', marginTop: 10, gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
              {i.mesaRegalos && p.regalos?.tiendaUrl && <GiftCard icon="store" title={REGALO_TARJETAS.mesaRegalos} description="Hemos preparado una lista en tienda." href={p.regalos.tiendaUrl} actionLabel="Abrir mesa" />}
              {i.transferencia && <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferencia} description={[p.regalos?.banco, p.regalos?.titular].filter(Boolean).join(' · ')} detail={p.regalos?.cuenta} detailLabel="Número de cuenta" actionLabel="Copiar número" />}
              {i.transferenciaQR && p.regalos?.qrUrl && <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferenciaQR} description="Desde la app de tu banco." qr={p.regalos.qrUrl} />}
              {i.efectivoSobre && <GiftCard icon="mail" title={REGALO_TARJETAS.efectivoSobre} description="Si prefieres efectivo, habrá un buzón a la entrada de la recepción." />}
            </div>
            {i.listaRegalos && p.regalos?.sugerencias && p.regalos.sugerencias.length > 0 && (
              <div style={{ width: '100%', maxWidth: 620, marginTop: 'clamp(18px,2vw,30px)', display: 'grid', gap: 'clamp(18px,2vw,26px)', justifyItems: 'center' }}>
                <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,21px)', color: C.muted }}>Algunas ideas, por si te sirven</p>
                <GiftList items={p.regalos.sugerencias.map((s) => ({ name: s.nombre, price: s.precio, description: s.descripcion }))} currency={p.regalos.moneda ?? 'Bs'} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* 14 · Confirmación (🔗) */}
      {p.invitado && (
        <section style={{ position: 'relative', padding: SEC_PAD, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-52%)', color: C.gold, opacity: .05, pointerEvents: 'none' }}><Filigree name="arco" width={360} /></div>
          <div style={{ position: 'relative', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', display: 'grid', justifyItems: 'center', gap: 16, marginBottom: 'clamp(34px,4vw,54px)' }}>
              <Script>{ROTULOS.confirmacion}</Script>
              <Diamond />
              {!rsvpClosed && p.rsvp?.fechaLimite && <p style={{ margin: 0, maxWidth: 400, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', lineHeight: 1.75, color: C.sec }}>Nos ayudaría muchísimo saberlo antes del {p.rsvp.fechaLimite}.</p>}
            </div>
            {rsvpClosed ? (
              <RsvpCerrado coordinadorUrl={coordinador?.url} coordinadorNombre={coordinador?.nombre} />
            ) : (
              <RsvpForm guest={p.invitado.nombre} maxPasses={p.invitado.pases} deadline={p.rsvp?.fechaLimite} submitted={rsvpSubmitted} onSubmit={onRsvp} />
            )}
          </div>
        </section>
      )}

      {/* 15 · Cierre */}
      <footer style={{ position: 'relative', padding: 'clamp(54px,6vw,94px) clamp(24px,6vw,110px)', textAlign: 'center', background: C.dark, color: C.onDark, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(58% 70% at 50% 40%,rgba(216,189,135,.16),transparent 72%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'grid', justifyItems: 'center', gap: 'clamp(24px,2.6vw,36px)' }}>
          <Monogram left={(n1.nombreCorto || '').charAt(0)} right={(n2.nombreCorto || '').charAt(0)} tone="onDark" />
          <p style={{ margin: 0, fontFamily: F.script, fontSize: 'clamp(48px,5.6vw,84px)', lineHeight: 1.28, padding: '0 .1em .12em', color: C.onDark }}>{Names}</p>
          {d.valida && <p style={{ margin: 0, fontFamily: F.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: 'rgba(240,227,202,.6)' }}>Gracias por acompañarnos · {d.fechaLarga}</p>}
        </div>
      </footer>
    </div>
  );
};

export default BodaElegante;
